import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { installAuthInterceptor } from '@/lib/authInterceptor';

// Install once at module load so the first fetch fires through it.
installAuthInterceptor();
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import MemberDirectory from '@/pages/MemberDirectory';
import EventsPage from '@/pages/EventsPage';
import EventDetailPage from '@/pages/EventDetailPage';
import EventAgendaPage from '@/pages/EventAgendaPage';
import JobsPage from '@/pages/JobsPage';
import CoursesPage from '@/pages/CoursesPage';
import CourseDetailPage from '@/pages/CourseDetailPage';
import CourseBuilderPage from '@/pages/CourseBuilderPage';
import MessagesPage from '@/pages/MessagesPage';
import KeynotesPage from '@/pages/KeynotesPage';
import BriteCastPage from '@/pages/BriteCastPage';
import NewsPage from '@/pages/NewsPage';
import NewsArticlePage from '@/pages/NewsArticlePage';
import SponsorsPage from '@/pages/SponsorsPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPanel from '@/pages/AdminPanel';
import AdminCheckInPage from '@/pages/AdminCheckInPage';
import PricingPage from '@/pages/PricingPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import BriteLabsPage from '@/pages/BriteLabsPage';
import BriteLabDetailPage from '@/pages/BriteLabDetailPage';
import AssistantPage from '@/pages/AssistantPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import PublicProfilePage from '@/pages/PublicProfilePage';
import FeedbackPage from '@/pages/FeedbackPage';
import SponsorPortalPage from '@/pages/SponsorPortalPage';
import ForumPage from '@/pages/ForumPage';
import PartnerDirectoryPage from '@/pages/PartnerDirectoryPage';
import CompanyProfilePage from '@/pages/CompanyProfilePage';
import PartnerTeamPage from '@/pages/PartnerTeamPage';
import CompanyDirectoryPage from '@/pages/CompanyDirectoryPage';
import CompanyDetailPage from '@/pages/CompanyDetailPage';
import ResearchLibraryPage from '@/pages/ResearchLibraryPage';
import DocsPage from '@/pages/DocsPage';
import NotificationsPage from '@/pages/NotificationsPage';
import GovernancePage from '@/pages/GovernancePage';
import { GlobalConfirmDialog } from '@/lib/appConfirm';
import KellyBubble from '@/components/KellyBubble';
import PageFooterBanner from '@/components/PageFooterBanner';
import ProfileCompletionBanner from '@/components/ProfileCompletionBanner';
import AuthCallback from '@/components/AuthCallback';
import CookieConsent from '@/components/CookieConsent';
import GlobalSideBanner from '@/components/GlobalSideBanner';
import ConsentModal from '@/components/ConsentModal';
import ProfileSetupGate from '@/components/onboarding/ProfileSetupGate';
import SiteFooter from '@/components/SiteFooter';
import '@/App.css';

function AppRouter() {
  const location = useLocation();

  // Reset scroll position to the top on every route change so navigating
  // between pages always lands the user at the page header — prevents the
  // perceived "jump" where the new page renders with the previous page's
  // scroll offset still applied.
  useEffect(() => {
    // `auto` (not `smooth`) so the jump is instant and matches a fresh load.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/profile/public/:userId" element={<PublicProfilePage />} />
      <Route path="/dashboard" element={<Navigate to="/progress" replace />} />
      <Route path="/progress" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MemberDirectory /></ProtectedRoute>} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/events/:eventId" element={<EventDetailPage />} />
      <Route path="/events/:eventId/agenda" element={<EventAgendaPage />} />
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId" element={<ProtectedRoute><CourseDetailPage /></ProtectedRoute>} />
      <Route path="/admin/courses/new" element={<ProtectedRoute><CourseBuilderPage /></ProtectedRoute>} />
      <Route path="/admin/courses/:courseId/edit" element={<ProtectedRoute><CourseBuilderPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/keynotes" element={<ProtectedRoute><KeynotesPage /></ProtectedRoute>} />
      <Route path="/britecast" element={<ProtectedRoute><BriteCastPage /></ProtectedRoute>} />
      <Route path="/news" element={<ProtectedRoute><NewsPage /></ProtectedRoute>} />
      <Route path="/news/:newsId" element={<ProtectedRoute><NewsArticlePage /></ProtectedRoute>} />
      <Route path="/sponsors" element={<ProtectedRoute><SponsorsPage /></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><PartnerDirectoryPage /></ProtectedRoute>} />
      <Route path="/partners/:sponsorId" element={<ProtectedRoute><CompanyProfilePage /></ProtectedRoute>} />
      <Route path="/partners/:sponsorId/team" element={<ProtectedRoute><PartnerTeamPage /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><CompanyDirectoryPage /></ProtectedRoute>} />
      <Route path="/companies/:companyId" element={<ProtectedRoute><CompanyDetailPage /></ProtectedRoute>} />
      <Route path="/learning" element={<ProtectedRoute><ResearchLibraryPage /></ProtectedRoute>} />
      <Route path="/research" element={<Navigate to="/learning" replace />} />
      <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
      <Route path="/forum/archive" element={<ProtectedRoute><ForumPage isArchive /></ProtectedRoute>} />
      <Route path="/sponsor" element={<ProtectedRoute><SponsorPortalPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/britelabs" element={<ProtectedRoute><BriteLabsPage /></ProtectedRoute>} />
      <Route path="/britelabs/:labId" element={<ProtectedRoute><BriteLabDetailPage /></ProtectedRoute>} />
      <Route path="/assistant" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/check-in" element={<ProtectedRoute><AdminCheckInPage /></ProtectedRoute>} />
      <Route path="/docs" element={<ProtectedRoute><DocsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/governance/:slug" element={<GovernancePage />} />
    </Routes>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fast path 1: user passed via navigation state
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Fast path 2: user cached in storage — trust it immediately
    const cachedUser = sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user');
    const storedToken = sessionStorage.getItem('session_token') || localStorage.getItem('sll_session_token');
    if (cachedUser && storedToken) {
      try {
        sessionStorage.setItem('session_token', storedToken);
        sessionStorage.setItem('sll_user', cachedUser);
        setUser(JSON.parse(cachedUser));
        setIsAuthenticated(true);
        // Background refresh — NEVER log out on failure (production proxies may interfere)
        fetch('/api/auth/session', { credentials: 'include' })
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(freshUser) {
            if (freshUser) {
              setUser(freshUser);
              var s = JSON.stringify(freshUser);
              sessionStorage.setItem('sll_user', s);
              localStorage.setItem('sll_user', s);
            }
          })
          .catch(function() { /* silently ignore — cached data is still valid */ });
        return;
      } catch(e) { /* fall through */ }
    }

    // No cached data — must verify with backend
    var checkAuth = async function() {
      try {
        var response = await fetch('/api/auth/session', { credentials: 'include' });
        if (!response.ok) {
          sessionStorage.removeItem('session_token');
          sessionStorage.removeItem('sll_user');
          localStorage.removeItem('sll_session_token');
          localStorage.removeItem('sll_user');
          throw new Error('Not authenticated');
        }
        var userData = await response.json();
        setUser(userData);
        var userStr = JSON.stringify(userData);
        sessionStorage.setItem('sll_user', userStr);
        localStorage.setItem('sll_user', userStr);
        setIsAuthenticated(true);
      } catch(error) {
        setIsAuthenticated(false);
        // Preserve the deep-link the user was trying to reach (e.g.
        // /news?article=news_xyz from a shared link) so they're returned
        // there after login instead of being dumped on the dashboard.
        try {
          var here = (location.pathname || '/') + (location.search || '');
          if (here && here !== '/' && !here.startsWith('/login')) {
            sessionStorage.setItem('post_login_redirect', here);
            navigate('/login?next=' + encodeURIComponent(here));
            return;
          }
        } catch (e) {}
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    const handleUserUpdate = () => {
      const cached = sessionStorage.getItem('sll_user');
      if (cached) {
        try { setUser(JSON.parse(cached)); } catch {}
      }
    };
    window.addEventListener('user-updated', handleUserUpdate);
    return () => window.removeEventListener('user-updated', handleUserUpdate);
  }, []);

  // Probe consent status whenever we know who the user is. The modal blocks
  // dismissal until both required boxes are ticked. Admin route is allowed
  // even before consent to prevent locking admins out of the editor.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    fetch('/api/consent/status', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.needs_consent) setNeedsConsent(true); })
      .catch(() => {});
  }, [isAuthenticated, user]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated && user?.profile_setup_required) {
    return <ProfileSetupGate user={user} onDone={setUser} />;
  }

  return isAuthenticated ? (
    <>
      {React.cloneElement(children, { user })}
      <PageFooterBanner />
      <ProfileCompletionBanner user={user} />
      <KellyBubble />
      <SiteFooter />
      <ConsentModal open={needsConsent} onComplete={() => setNeedsConsent(false)} />
    </>
  ) : null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <GlobalSideBanner />
        <CookieConsent />
        <Toaster richColors position="top-right" />
        <GlobalConfirmDialog />
      </BrowserRouter>
    </div>
  );
}

export default App;
